import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Info, Loader2 } from 'lucide-react';
import { Button, TileRadioGroup } from '@ai-tutor/ui';
import { notify } from '@/lib/notify';
import { type LessonDepth, type LessonSettings } from '@/lib/api-client';
import {
  useUpdateUserLessonSettings,
  useUserLessonSettings,
} from '@/hooks/account/useUserLessonSettings';
import { LessonFlowSection } from './LessonFlowSection';
import { LearningProfileCard } from './LearningProfileCard';
import { LearningStyleSkeleton } from '../SettingsSkeleton';
import { DEPTH_OPTIONS, DEFAULT_DEPTH } from '@/config/lesson-settings-options';

/** The shared option set carries `sub`; the tiles want `description`. */
const DEPTH_TILES = DEPTH_OPTIONS.map(o => ({
  value: o.value,
  label: o.label,
  description: o.sub,
}));

export function LearningStyleSection() {
  const { data, isLoading } = useUserLessonSettings();
  const updateSettings = useUpdateUserLessonSettings();

  // Hydrate from server, fall back to standard if the user has never
  // touched the setting before. Track baseline separately so we can show
  // a Save button only when the picked depth differs.
  const [depth, setDepth] = useState<LessonDepth>(DEFAULT_DEPTH);

  const baselineDepth: LessonDepth =
    data?.default_lesson_settings.options?.depth ?? DEFAULT_DEPTH;
  const isLoaded = data !== undefined;

  // Keyed on the stored DEPTH, not on the `data` object. The Lesson Flow card
  // below saves into the same query, and re-hydrating on every new `data`
  // silently reset an unsaved depth pick whenever the flow was saved.
  useEffect(() => {
    if (isLoaded) setDepth(baselineDepth);
  }, [isLoaded, baselineDepth]);

  const isDepthDirty = depth !== baselineDepth;

  const saveDepth = useMutation({
    mutationFn: () => {
      // The PUT replaces `default_lesson_settings` whole; preserve the
      // existing flow/custom_steps so the LessonFlow card's state isn't
      // clobbered when the user only edits depth here.
      const current = data?.default_lesson_settings ?? { flow: 'core' };
      const nextSettings: LessonSettings = {
        ...current,
        options: {
          ...(current.options ?? {}),
          depth,
        },
      };
      return updateSettings.mutateAsync({
        default_lesson_settings: nextSettings,
      });
    },
    onSuccess: () => notify.success('Lesson depth saved'),
    // `err` itself, not `err.message`: as a bare string it skipped notify's
    // status-aware copy and a 500 toasted "HTTP 500: Internal Server Error".
    onError: (err: unknown) => {
      notify.error(err, "Couldn't save your lesson depth");
    },
  });

  if (isLoading) {
    return <LearningStyleSkeleton />;
  }

  return (
    <div className='space-y-8'>
      {/* Learning profile - role/level/subjects/goal captured at onboarding,
          editable here. Saves via PUT /me/onboarding (idempotent). */}
      <LearningProfileCard />

      {/* Lesson Depth - the only directly-wired generation knob today.
          Persisted as `default_lesson_settings.options.depth`; byte
          selection + text generation both read it. */}
      <div className='rounded-lg border border-border p-5'>
        <div className='mb-1 flex items-center gap-1.5'>
          <h3 className='text-sm font-semibold text-foreground'>
            Lesson Depth
          </h3>
          {/* Hover/focus to reveal the concrete budgets. Keyboard
              users get it via `focus-within`. No Radix dep needed -
              the tooltip is a positioned <div> driven by `group-hover`
              + `group-focus-within`. */}
          <span
            tabIndex={0}
            role='button'
            aria-label='What each depth means'
            className='group relative inline-flex cursor-help items-center rounded-full text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:text-foreground'
          >
            <Info className='h-3.5 w-3.5' />
            <div className='pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-64 -translate-x-1/2 rounded-md border border-border bg-card p-3 text-xs text-muted-foreground opacity-0 shadow-lg transition-opacity duration-150 group-focus-within:opacity-100 group-hover:opacity-100'>
              <div className='mb-2 font-semibold text-foreground'>
                What each depth does
              </div>
              <ul className='space-y-1.5'>
                {DEPTH_OPTIONS.map(option => (
                  <li key={option.value}>
                    <span className='font-medium'>{option.label}</span>:{' '}
                    <span className='text-muted-foreground'>
                      about {option.wordsPerConcept} words,{' '}
                      {option.questionsPerConcept} quiz question
                      {option.questionsPerConcept === 1 ? '' : 's'} and{' '}
                      {option.questionsPerConcept} flashcard
                      {option.questionsPerConcept === 1 ? '' : 's'} per concept
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </span>
        </div>
        <p className='mb-5 text-xs text-muted-foreground'>
          Every concept in your material is always covered. Depth sets how fully
          each one is explained and practised.
        </p>

        {/* `TileRadioGroup`, not a hand-rolled grid. This card sits directly
            under the role picker, which renders through the same component, and
            the two had drifted into different-looking tiles on one page. It
            also brings the radiogroup semantics the hand-rolled version never
            had: one tab stop, arrow keys, and a group name. `labelHidden`
            because the heading above already asks the question. */}
        <TileRadioGroup
          label='Lesson depth'
          labelHidden
          options={DEPTH_TILES}
          value={depth}
          onChange={setDepth}
          columns={3}
        />

        <div className='mt-5'>
          <Button
            size='sm'
            className='max-sm:h-11'
            onClick={() => saveDepth.mutate()}
            disabled={!isDepthDirty || saveDepth.isPending}
          >
            {saveDepth.isPending ? (
              <>
                <Loader2 className='mr-2 h-3.5 w-3.5 animate-spin' />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </div>

      {/* Lesson Flow - existing LessonFlowSection (default flow + saved
          custom flows) wrapped in a matching card. Its internal logic
          and Save buttons are untouched. */}
      <div className='rounded-lg border border-border p-5'>
        <h3 className='mb-1 text-sm font-semibold text-foreground'>
          Lesson Flow
        </h3>
        <p className='mb-5 text-xs text-muted-foreground'>
          Choose which artifacts get generated for each lesson and manage
          reusable flow templates.
        </p>
        <LessonFlowSection />
      </div>
    </div>
  );
}
